import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useCompany } from '../context/CompanyContext';
import { INDIAN_STATES } from '../config/geoData';

const FIELD_GROUPS = [
  {
    title: 'Company Identity',
    fields: [
      { key: 'name', label: 'Company Name', type: 'text' },
      { key: 'shortName', label: 'Short Name', type: 'text' },
      { key: 'tagline', label: 'Tagline', type: 'text' },
      { key: 'website', label: 'Website', type: 'text' },
    ],
  },
  {
    title: 'Contact Information',
    fields: [
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'address', label: 'Full Address', type: 'textarea' },
      { key: 'hours', label: 'Business Hours', type: 'text' },
    ],
  },
  {
    title: 'Legal & Tax Identifiers',
    fields: [
      { key: 'gstin', label: 'GSTIN', type: 'text' },
      { key: 'cin', label: 'CIN', type: 'text' },
      { key: 'iec', label: 'IEC', type: 'text' },
      { key: 'pan', label: 'PAN', type: 'text' },
      { key: 'msme', label: 'MSME Number', type: 'text' },
      { key: 'lut', label: 'LUT Number', type: 'text' },
      { key: 'lutDate', label: 'LUT Date', type: 'text' },
      { key: 'stateCode', label: 'GST State Code', type: 'text' },
      { 
        key: 'state', label: 'State', type: 'select', 
        options: ['Outside India / Others', ...INDIAN_STATES.map(s => s.name)],
        allowEmpty: true
      },
    ],
  },
  {
    title: 'Bank Details',
    fields: [
      { key: 'bankName', label: 'Bank Name', type: 'text' },
      { key: 'bankAccount', label: 'Account No.', type: 'text' },
      { key: 'bankIfsc', label: 'IFSC Code', type: 'text' },
      { key: 'bankBranch', label: 'Branch', type: 'text' },
    ],
  },
  {
    title: 'Insurance',
    fields: [
      { key: 'insurancePolicy', label: 'Policy No.', type: 'text' },
      { key: 'insuranceType', label: 'Insurance Type', type: 'text' },
      { key: 'insuranceCompany', label: 'Insurance Co.', type: 'text' },
    ],
  },
  {
    title: 'Currency & Finance',
    fields: [
      {
        key: 'default_currency', label: 'Default Currency', type: 'select',
        options: ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'JPY'],
        hint: 'Default currency for new orders, payments, and expenses. Used in all financial reports.'
      },
      {
        key: 'usd_to_inr_rate', label: 'USD → INR Exchange Rate', type: 'number',
        hint: 'Reference rate used for currency conversion in reports (e.g. 84). Update periodically.'
      },
    ],
  },
  {
    title: 'Payment Configuration',
    fields: [
      {
        key: 'showPaymentOnInvoice', label: 'Show Payment QR on Invoice PDF', type: 'toggle',
        hint: 'When enabled, the PAY NOW QR code and payment link appear on Invoice and Proforma Invoice PDFs.'
      },
      {
        key: 'paymentLink', label: 'Payment Gateway URL (prefix)', type: 'text',
        hint: 'Base URL for the PAY NOW QR code on invoices. The invoice number is appended automatically (e.g. https://pay.example.com/inv/).'
      },
      {
        key: 'paymentQrLabel', label: 'Payment QR Label', type: 'text',
        hint: 'Short label shown below the PAY NOW QR code on invoice PDFs (e.g. "Pay via Razorpay" or "Scan to pay — UPI / Cards").'
      },
    ],
  },
  {
    title: 'Orders & Workflow',
    fields: [
      { key: 'enableCloneOrder', label: 'Enable Clone Order Button', type: 'toggle',
        hint: 'When enabled, a Clone Order button appears in the Orders table. Disabled by default.' },
      { key: 'enableSplitOrder', label: 'Enable Split Order Button', type: 'toggle',
        hint: 'When enabled, a Split Order button appears in the Orders table and order detail view, allowing an order to be split into multiple partial shipments. Disabled by default.' },
    ],
  },
  {
    title: 'Document Numbering — Prefixes',
    fields: [
      { key: 'doc_prefix_invoice', label: 'Invoice / Tax Invoice Prefix', type: 'text',
        hint: 'Short code printed before the document number. Default: PL → generates PL/25-26/001' },
      { key: 'doc_prefix_purchase_order', label: 'Purchase Order Prefix', type: 'text',
        hint: 'Default: PO → generates PO/25-26/001' },
      { key: 'doc_prefix_proforma_invoice', label: 'Proforma Invoice Prefix', type: 'text',
        hint: 'Default: PI → generates PI/25-26/001' },
      { key: 'doc_prefix_inquiry', label: 'Inquiry / Quote Prefix', type: 'text',
        hint: 'Default: PQ → generates PQ/25-26/001' },
    ],
  },
  {
    title: 'Social & Contact Links',
    fields: [
      { key: 'linkedin', label: 'LinkedIn URL', type: 'text' },
      { key: 'whatsapp', label: 'WhatsApp URL', type: 'text' },
    ],
  },
  {
    title: 'Employee ID Settings',
    fields: [
      {
        key: 'employee_id_prefix', label: 'Employee ID Prefix', type: 'text',
        hint: 'Prefix for employee codes (e.g. "PL" generates PL-001, PL-002, …). Uppercase letters/numbers only, max 10 chars.'
      },
      {
        key: 'employee_id_next', label: 'Next Sequence Start', type: 'number',
        hint: 'Override the minimum sequence number for the next new employee. The system picks max(current highest + 1, this value). Set to 1 to let the DB sequence decide.'
      },
      { key: '_employee_id_preview', label: 'Preview', type: 'preview' },
    ],
  },
];

export default function CompanySettings() {
  const { refreshSettings } = useCompany();
  const [form, setForm] = useState({});
  const [exporters, setExporters] = useState([]);   // [{ name, accountNo }]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);  // { type: 'success'|'error', text }

  // Document sequences state
  const [sequences, setSequences] = useState([]);
  const [seqEdit, setSeqEdit] = useState({});   // { [prefix]: string } — editable next values
  const [seqSaving, setSeqSaving] = useState(null);
  const [seqMsg, setSeqMsg] = useState(null);

  const loadSequences = useCallback(async () => {
    try {
      const { data } = await api.get('/company-settings/sequences');
      setSequences(data.data || []);
      const edits = {};
      (data.data || []).forEach(s => { edits[s.prefix] = String(s.seq + 1); });
      setSeqEdit(edits);
    } catch { /* sequences panel stays empty */ }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/company-settings');
      const d = data.data || {};
      setExporters(Array.isArray(d.exporters) ? d.exporters : []);
      setForm(d);
    } catch {
      setMsg({ type: 'error', text: 'Failed to load settings.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadSequences(); }, [load, loadSequences]);

  const handleChange = (key, value) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleExporterChange = (idx, field, value) =>
    setExporters(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));

  const addExporter = () =>
    setExporters(prev => [...prev, { name: '', accountNo: '' }]);

  const removeExporter = (idx) =>
    setExporters(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      await api.put('/company-settings', { ...form, exporters });
      setMsg({ type: 'success', text: 'Company settings saved successfully.' });
      refreshSettings();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSeqSave = async (prefix) => {
    const next = parseInt(seqEdit[prefix], 10);
    if (isNaN(next) || next < 1) {
      setSeqMsg({ type: 'error', text: 'Next number must be a positive integer.' });
      return;
    }
    setSeqSaving(prefix);
    setSeqMsg(null);
    try {
      const { data } = await api.put('/company-settings/sequences', { prefix, next });
      setSequences(data.data || []);
      const edits = {};
      (data.data || []).forEach(s => { edits[s.prefix] = String(s.seq + 1); });
      setSeqEdit(edits);
      setSeqMsg({ type: 'success', text: `${prefix} sequence updated — next number: ${next}.` });
    } catch (err) {
      setSeqMsg({ type: 'error', text: err.response?.data?.message || 'Failed to update sequence.' });
    } finally {
      setSeqSaving(null);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
      <div className="spinner" />
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '8px 0 40px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          Company Settings
        </h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: 4, fontSize: '0.875rem' }}>
          Edit company information used across PDFs, invoices, and the public website.
        </p>
      </div>

      {msg && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 20, fontSize: '0.875rem',
          background: msg.type === 'success' ? 'var(--success-lighter, #d1fae5)' : 'var(--danger-lighter, #fee2e2)',
          color: msg.type === 'success' ? 'var(--success, #059669)' : 'var(--danger, #dc2626)',
          border: `1px solid ${msg.type === 'success' ? '#6ee7b7' : '#fca5a5'}`,
        }}>
          {msg.text}
        </div>
      )}

      <form onSubmit={handleSave}>
        {FIELD_GROUPS.map(group => (
          <div key={group.title} className="card" style={{ marginBottom: 20 }}>
            <div style={{
              padding: '14px 20px', borderBottom: '1px solid var(--border)',
              fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)',
              background: 'var(--surface-2, #f9fafb)', borderRadius: '8px 8px 0 0',
            }}>
              {group.title}
            </div>
            <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>
              {group.fields.map(f => (
                <div key={f.key} style={{ gridColumn: f.type === 'textarea' ? '1 / -1' : undefined }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5 }}>
                    {f.label}
                  </label>
                  {f.type === 'toggle' ? (() => {
                    const isOn = form[f.key] === true || form[f.key] === 1;
                    return (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 2 }}>
                        <div
                          onClick={() => handleChange(f.key, !isOn)}
                          style={{
                            width: 40, height: 22, borderRadius: 11, cursor: 'pointer', transition: 'background 0.2s',
                            background: isOn ? 'var(--primary, #6366f1)' : 'var(--border, #d1d5db)',
                            position: 'relative', flexShrink: 0,
                          }}>
                          <div style={{
                            position: 'absolute', top: 3, left: isOn ? 21 : 3,
                            width: 16, height: 16, borderRadius: '50%', background: '#fff',
                            transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          }} />
                        </div>
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', fontWeight: 400 }}>
                          {isOn ? (f.onLabel || 'Enabled') : (f.offLabel || 'Disabled')}
                        </span>
                      </label>
                    );
                  })() : f.type === 'textarea' ? (
                    <textarea
                      rows={3}
                      value={form[f.key] || ''}
                      onChange={e => handleChange(f.key, e.target.value)}
                      style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
                      className="input"
                    />
                  ) : f.type === 'select' ? (
                    <select
                      value={form[f.key] ?? (f.allowEmpty ? '' : f.options[0])}
                      onChange={e => handleChange(f.key, e.target.value)}
                      className="input"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    >
                      {f.allowEmpty && <option value="">— Select —</option>}
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === 'preview' ? (() => {
                    const pfx = ((form.employee_id_prefix || 'PL').toUpperCase().replace(/[^A-Z0-9]/g, '') || 'PL');
                    const seq = Math.max(1, parseInt(form.employee_id_next, 10) || 1);
                    const sample = `${pfx}-${String(seq).padStart(3, '0')}`;
                    return (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, marginTop: 2,
                        padding: '6px 12px', borderRadius: 7,
                        background: 'var(--surface-2, #f1f5f9)', border: '1px solid var(--border)',
                      }}>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Next ID will look like:</span>
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.9rem', color: 'var(--primary, #6366f1)', letterSpacing: '0.05em' }}>
                          {sample}
                        </span>
                      </div>
                    );
                  })() : (
                    <input
                      type={f.type}
                      value={form[f.key] || ''}
                      onChange={e => handleChange(f.key, e.target.value)}
                      className="input"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                  )}
                  {f.hint && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{f.hint}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Exporters */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{
            padding: '14px 20px', borderBottom: '1px solid var(--border)',
            fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)',
            background: 'var(--surface-2, #f9fafb)', borderRadius: '8px 8px 0 0',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span>Exporters &amp; Account Numbers</span>
            <button type="button" onClick={addExporter}
              style={{
                display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 600,
                padding: '4px 12px', borderRadius: 7, border: '1px solid var(--primary, #6366f1)',
                background: 'var(--primary-lighter, #eef2ff)', color: 'var(--primary, #6366f1)', cursor: 'pointer'
              }}>
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Exporter
            </button>
          </div>
          <div style={{ padding: '16px 20px' }}>
            {exporters.length === 0 ? (
              <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)', margin: 0 }}>
                No exporters added yet. Click "Add Exporter" to add one.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Header */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 36px', gap: '8px 12px' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Exporter Name</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Account Number</span>
                  <span />
                </div>
                {exporters.map((exp, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 36px', gap: '8px 12px', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="e.g. PRASI LABS Pvt Ltd"
                      value={exp.name}
                      onChange={e => handleExporterChange(idx, 'name', e.target.value)}
                      className="input"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                    <input
                      type="text"
                      placeholder="e.g. 1234567890"
                      value={exp.accountNo}
                      onChange={e => handleExporterChange(idx, 'accountNo', e.target.value)}
                      className="input"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                    <button type="button" onClick={() => removeExporter(idx)}
                      title="Remove"
                      style={{
                        width: 32, height: 32, borderRadius: 7, border: '1px solid #fca5a5',
                        background: '#fff1f2', color: '#dc2626', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                      }}>
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-secondary" onClick={load} disabled={saving}>
            Reset
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* ── Document Sequence Manager ── */}
      <div className="card" style={{ marginTop: 28, marginBottom: 20 }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--border)',
          fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)',
          background: 'var(--surface-2, #f9fafb)', borderRadius: '8px 8px 0 0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>Document Sequence Numbers</span>
          <button type="button" className="btn btn-secondary" style={{ fontSize: '0.78rem', padding: '4px 12px' }}
            onClick={loadSequences}>
            ↻ Refresh
          </button>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14 }}>
            Current counters for the active financial year. Change <strong>Next Number</strong> to reset or advance
            a sequence — the very next document created will use that number.
            The prefix shown here reflects the <em>current prefix in use</em> (from the DB counter) — to change the prefix,
            update the Document Numbering settings above and save.
          </p>
          {seqMsg && (
            <div style={{
              padding: '8px 14px', borderRadius: 7, marginBottom: 14, fontSize: '0.82rem',
              background: seqMsg.type === 'success' ? 'var(--success-lighter, #d1fae5)' : '#fee2e2',
              color: seqMsg.type === 'success' ? 'var(--success, #059669)' : '#dc2626',
              border: `1px solid ${seqMsg.type === 'success' ? '#6ee7b7' : '#fca5a5'}`,
            }}>
              {seqMsg.text}
            </div>
          )}
          {sequences.length === 0 ? (
            <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)' }}>
              No sequences yet — they are created automatically when the first document of each type is generated.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-secondary)', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase' }}>Prefix</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-secondary)', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase' }}>FY</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', color: 'var(--text-secondary)', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase' }}>Last Issued</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text-secondary)', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase' }}>Last Number</th>
                  <th style={{ padding: '6px 10px', color: 'var(--text-secondary)', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase' }}>Set Next Number</th>
                </tr>
              </thead>
              <tbody>
                {sequences.map((s, i) => (
                  <tr key={s.prefix} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--surface-2, #f9fafb)' }}>
                    <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary, #6366f1)' }}>{s.prefix}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{s.fy}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-secondary)' }}>{s.seq}</td>
                    <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                      {s.prefix}/{s.fy}/{s.seq < 1000 ? String(s.seq).padStart(3, '0') : s.seq}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          type="number"
                          min={1}
                          value={seqEdit[s.prefix] || ''}
                          onChange={e => setSeqEdit(prev => ({ ...prev, [s.prefix]: e.target.value }))}
                          className="input"
                          style={{ width: 90, padding: '4px 8px', fontSize: '0.84rem', fontFamily: 'monospace' }}
                          placeholder="e.g. 50"
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ fontSize: '0.78rem', padding: '4px 12px', flexShrink: 0 }}
                          disabled={seqSaving === s.prefix}
                          onClick={() => handleSeqSave(s.prefix)}
                        >
                          {seqSaving === s.prefix ? '…' : 'Set'}
                        </button>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          → next: <strong style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                            {s.prefix}/{s.fy}/{(() => { const n = parseInt(seqEdit[s.prefix], 10); return isNaN(n) || n < 1 ? '?' : (n < 1000 ? String(n).padStart(3, '0') : n); })()}
                          </strong>
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
